import { getAPI, postAPI, putAPI, deleteAPI } from "../utils/api";
import endpoints from "../utils/endpoints";

/**
 * Fetch featured section data including member items
 * GET /api/featured-section?section=featured&onlyActive=false
 */
export const getFeaturedSection = (
  sectionKey = "featured",
  onlyActive = false,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    const params = {
      section: sectionKey,
      onlyActive: Boolean(onlyActive),
    };
    getAPI(
      endpoints.FEATURED_SECTION,
      params,
      (response) => {
        if (typeof callBack === "function") callBack(response);
      },
      (error) => {
        if (typeof errorCallback === "function") errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};

/**
 * Fetch section metadata (name, description, active status, etc.)
 * GET /api/featured-section/section?section=featured
 */
export const getSectionMetadata = (
  sectionKey = "featured",
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    getAPI(
      endpoints.FEATURED_SECTION_METADATA,
      { section: sectionKey },
      (response) => {
        if (typeof callBack === "function") callBack(response);
      },
      (error) => {
        if (typeof errorCallback === "function") errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};

/**
 * Update section metadata (name, description, active)
 * PUT /api/featured-section/section?section=featured
 */
export const updateSectionMetadata = (
  sectionKey = "featured",
  payload = {},
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    const query = new URLSearchParams({ section: sectionKey }).toString();
    const url = `${endpoints.FEATURED_SECTION_METADATA}?${query}`;
    putAPI(
      url,
      payload,
      (response) => {
        if (typeof callBack === "function") callBack(response);
      },
      (error) => {
        if (typeof errorCallback === "function") errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};

/**
 * Quick update of section name/title
 * PUT /api/featured-section/title?name=...&section=featured
 */
export const updateSectionTitle = (
  sectionKey = "featured",
  name = "",
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    const params = new URLSearchParams({
      section: sectionKey,
      name,
    }).toString();
    const url = `${endpoints.FEATURED_SECTION_TITLE}?${params}`;
    putAPI(
      url,
      {},
      (response) => {
        if (typeof callBack === "function") callBack(response);
      },
      (error) => {
        if (typeof errorCallback === "function") errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};

/**
 * List featured items with pagination, filtering, and search
 * GET /api/featured-section/items?page=0&size=20&section=featured&search=...&active=...
 */
export const getFeaturedItems = (
  params = {},
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    const queryParams = {
      page: params.page ?? 0,
      size: params.size ?? 20,
      section: params.section || "featured",
    };
    if (params.search && params.search.trim()) {
      queryParams.search = params.search.trim();
    }
    if (typeof params.active === "boolean") {
      queryParams.active = params.active;
    }
    getAPI(
      endpoints.FEATURED_ITEMS,
      queryParams,
      (response) => {
        if (typeof callBack === "function") callBack(response);
      },
      (error) => {
        if (typeof errorCallback === "function") errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};

/**
 * Get featured item by ID
 * GET /api/featured-section/items/{id}
 */
export const getFeaturedItemById = (
  id,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    const url = endpoints.FEATURED_ITEM_BY_ID.replace(":id", id);
    getAPI(
      url,
      {},
      (response) => {
        if (typeof callBack === "function") callBack(response);
      },
      (error) => {
        if (typeof errorCallback === "function") errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};

/**
 * Create a new featured item
 * POST /api/featured-section/items
 * Supports JSON or multipart/form-data
 */
export const createFeaturedItem = (
  payload = {},
  file = null,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      const queryParams = new URLSearchParams();
      if (payload.name) queryParams.append("name", payload.name);
      if (payload.title) queryParams.append("title", payload.title);
      if (payload.link) queryParams.append("link", payload.link);
      if (payload.image) queryParams.append("image", payload.image);
      if (payload.displayOrder != null) {
        queryParams.append("displayOrder", payload.displayOrder);
      }
      queryParams.append(
        "active",
        typeof payload.active === "boolean" ? payload.active : true
      );
      queryParams.append("sectionKey", payload.sectionKey || "featured");

      const url = `${endpoints.FEATURED_ITEMS}?${queryParams.toString()}`;
      postAPI(
        url,
        formData,
        (response) => {
          if (typeof callBack === "function") callBack(response);
        },
        (error) => {
          if (typeof errorCallback === "function") errorCallback(error);
        },
        localStorage.getItem("token")
      );
    } else {
      const body = {
        name: payload.name || "",
        title: payload.title || "",
        link: payload.link || "",
        image: payload.image || "",
        displayOrder:
          payload.displayOrder != null ? Number(payload.displayOrder) : 1,
        active: typeof payload.active === "boolean" ? payload.active : true,
        sectionKey: payload.sectionKey || "featured",
      };
      postAPI(
        endpoints.FEATURED_ITEMS,
        body,
        (response) => {
          if (typeof callBack === "function") callBack(response);
        },
        (error) => {
          if (typeof errorCallback === "function") errorCallback(error);
        },
        localStorage.getItem("token")
      );
    }
  };
};

/**
 * Update an existing featured item
 * PUT /api/featured-section/items/{id}
 * Supports JSON or multipart/form-data
 */
export const updateFeaturedItem = (
  id,
  payload = {},
  file = null,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    const itemUrl = endpoints.FEATURED_ITEM_BY_ID.replace(":id", id);
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      const queryParams = new URLSearchParams();
      if (payload.name) queryParams.append("name", payload.name);
      if (payload.title) queryParams.append("title", payload.title);
      if (payload.link) queryParams.append("link", payload.link);
      if (payload.image) queryParams.append("image", payload.image);
      if (payload.displayOrder != null) {
        queryParams.append("displayOrder", payload.displayOrder);
      }
      if (typeof payload.active === "boolean") {
        queryParams.append("active", payload.active);
      }
      queryParams.append("sectionKey", payload.sectionKey || "featured");

      const url = `${itemUrl}?${queryParams.toString()}`;
      putAPI(
        url,
        formData,
        (response) => {
          if (typeof callBack === "function") callBack(response);
        },
        (error) => {
          if (typeof errorCallback === "function") errorCallback(error);
        },
        localStorage.getItem("token")
      );
    } else {
      const body = {
        name: payload.name || "",
        title: payload.title || "",
        link: payload.link || "",
        image: payload.image || "",
        displayOrder:
          payload.displayOrder != null ? Number(payload.displayOrder) : 1,
        active: typeof payload.active === "boolean" ? payload.active : true,
        sectionKey: payload.sectionKey || "featured",
      };
      putAPI(
        itemUrl,
        body,
        (response) => {
          if (typeof callBack === "function") callBack(response);
        },
        (error) => {
          if (typeof errorCallback === "function") errorCallback(error);
        },
        localStorage.getItem("token")
      );
    }
  };
};

/**
 * Delete a featured item
 * DELETE /api/featured-section/items/{id}
 */
export const deleteFeaturedItem = (
  id,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    const url = endpoints.FEATURED_ITEM_BY_ID.replace(":id", id);
    deleteAPI(
      url,
      {},
      (response) => {
        if (typeof callBack === "function") callBack(response);
      },
      (error) => {
        if (typeof errorCallback === "function") errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};

/**
 * Update display order of a single item
 * PUT /api/featured-section/items/{id}/order?displayOrder=...
 */
export const updateFeaturedItemDisplayOrder = (
  id,
  displayOrder,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    const url = `${endpoints.FEATURED_ITEM_ORDER.replace(":id", id)}?displayOrder=${encodeURIComponent(
      displayOrder
    )}`;
    putAPI(
      url,
      {},
      (response) => {
        if (typeof callBack === "function") callBack(response);
      },
      (error) => {
        if (typeof errorCallback === "function") errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};

/**
 * Batch reorder featured items
 * PUT /api/featured-section/items/reorder
 */
export const batchReorderFeaturedItems = (
  reorderList = [],
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    const payload = Array.isArray(reorderList) ? reorderList : [];
    putAPI(
      endpoints.FEATURED_ITEMS_REORDER,
      payload,
      (response) => {
        if (typeof callBack === "function") callBack(response);
      },
      (error) => {
        if (typeof errorCallback === "function") errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};
